import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Loader2,
  ArrowRight, RotateCcw, Database, ShieldQuestion, History, CalendarClock,
  Store, MapPin, Palette, GitCompareArrows, Users2, HeartHandshake, Split,
  Search, Plus, Link2, Ban, ShieldCheck, Euro, Building2, ChevronDown
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { SPOT_STATUS, frenchDate } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function ImportBDD() {
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "evg" ? "evg" : params.get("tab") === "hist" ? "hist" : "bdd";
  return (
    <>
      <PageHeader title="Imports"
        subtitle="Alimentez la plateforme : la base de spots (fichier Shariti), l'historique d'exploitation, et les bulletins Evergiving." />
      <PageBody>
        <Tabs defaultValue={initialTab}>
          <TabsList className="flex h-auto w-full max-w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="bdd" data-testid="import-tab-bdd" className="shrink-0"><Database className="mr-1.5 h-4 w-4" />Banque de spots</TabsTrigger>
            <TabsTrigger value="hist" data-testid="import-tab-hist" className="shrink-0"><History className="mr-1.5 h-4 w-4" />Historique (fichier de suivi)</TabsTrigger>
            <TabsTrigger value="evg" data-testid="import-tab-evg" className="shrink-0"><HeartHandshake className="mr-1.5 h-4 w-4" />Bulletins (Evergiving)</TabsTrigger>
          </TabsList>
          <TabsContent value="bdd" className="mt-5"><BddImportTab /></TabsContent>
          <TabsContent value="hist" className="mt-5"><HistoryImportTab /></TabsContent>
          <TabsContent value="evg" className="mt-5"><EvergivingImportTab /></TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

/* ============================ BDD (existant) ============================ */
function BddImportTab() {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [reports, setReports] = useState([]);
  const inputRef = useRef();

  useEffect(() => { api.get("/imports/reports").then((r) => setReports(r.data)).catch(() => {}); }, [result]);

  const upload = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur de lecture du fichier"); }
    finally { setLoading(false); }
  };
  const loadDemo = async () => {
    setLoading(true);
    try { const { data } = await api.post("/imports/preview-url", {}); setPreview(data); setStep(2); }
    catch (err) { toast.error(err?.response?.data?.detail || "Impossible de charger le fichier de démonstration."); }
    finally { setLoading(false); }
  };
  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/imports/commit", { import_id: preview.import_id });
      setResult(data); setStep(3); toast.success(`${data.created} spots importés`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la validation"); }
    finally { setCommitting(false); }
  };
  const reset = () => { setStep(1); setPreview(null); setResult(null); };
  const report = preview?.report;

  return (
    <>
      <Stepper step={step} labels={["Téléversement", "Prévisualisation", "Import"]} />
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Dropzone loading={loading} inputRef={inputRef} onFile={upload} testId="import" accept=".xlsx,.xlsm"
              title="Déposez votre fichier .xlsx ici" />
            <div className="mt-4 flex items-center justify-center">
              <Button variant="secondary" onClick={loadDemo} disabled={loading} className="border border-[var(--border-soft)] bg-white" data-testid="import-demo">
                <Database className="mr-2 h-4 w-4" /> Charger le fichier BDD fourni (démo)
              </Button>
            </div>
          </motion.div>
        )}
        {step === 2 && report && (
          <motion.div key="s2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-4">
              <FileSpreadsheet className="h-6 w-6 text-[var(--shariti-orange)]" />
              <div className="flex-1"><div className="font-bold">{preview.filename}</div>
                <div className="text-xs text-muted-ink">{report.total} spots prêts</div></div>
              <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm font-bold text-[var(--shariti-orange)]" data-testid="import-total">{report.total}</span>
            </div>
            <div className="grid gap-4 @4xl/page:grid-cols-3">
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 @4xl/page:col-span-2">
                <h3 className="mb-3 text-sm font-bold">Rapport d'import par statut</h3>
                <div className="grid grid-cols-2 gap-2 @3xl/page:grid-cols-3">
                  {Object.entries(report.status_counts).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid={`report-status-${k}`}>
                      <span className="text-xs font-semibold">{SPOT_STATUS[k]?.label || k}</span><span className="text-sm font-extrabold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <MiniStat icon={ShieldQuestion} label="Doublons détectés" value={report.duplicates?.length || 0} color="#EE722E" testId="report-duplicates" />
                <MiniStat icon={AlertTriangle} label="Lignes rejetées" value={report.rejected?.length || 0} color="#D64545" testId="report-rejected" />
                <MiniStat icon={AlertTriangle} label="Avertissements (tel/email)" value={report.warnings?.length || 0} color="#E9B800" testId="report-warnings" />
                <MiniStat icon={Copy} label="Spots payants" value={report.payant_count || 0} color="#2F6FED" testId="report-payant" />
              </div>
            </div>
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="report-by-sheet">
              <h3 className="mb-3 text-sm font-bold">Spots importés par feuille</h3>
              <div className="grid grid-cols-1 gap-2 @3xl/page:grid-cols-3">
                {Object.entries(report.sheets || {}).map(([sheet, n]) => (
                  <div key={sheet} className="flex items-center justify-between rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid={`report-sheet-${sheet}`}>
                    <span className="text-xs font-semibold">{sheet}</span><span className="text-sm font-extrabold">{n}</span>
                  </div>
                ))}
              </div>
            </div>
            <DetailsList items={report.duplicates} label="doublons" render={(d) => [`${d.name}`, `${d.sheet} • déjà vu ${d.first_seen}`]} />
            {report.rejected?.length > 0 && <DetailsList items={report.rejected} label="lignes rejetées" danger testId="report-rejected-details" render={(d) => [`${d.sheet} • ligne ${d.row}`, d.reason]} />}
            {report.warnings?.length > 0 && <DetailsList items={report.warnings} label="avertissements (tél./email)" warn testId="report-warnings-details" render={(d) => [`${d.sheet} • ligne ${d.row}`, d.reason]} />}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={reset} data-testid="import-cancel"><RotateCcw className="mr-2 h-4 w-4" /> Recommencer</Button>
              <Button onClick={commit} disabled={committing} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="import-commit">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Valider l'import de {report.total} spots <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </div>
          </motion.div>
        )}
        {step === 3 && result && (
          <SuccessCard title={`${result.created} spots importés`} subtitle="Votre base est à jour. Retrouvez les spots dans la banque." onReset={reset} testId="import-success" />
        )}
      </AnimatePresence>
      {reports.length > 0 && step === 1 && (
        <div className="mt-8">
          <h3 className="mb-2 text-sm font-bold text-muted-ink">Historique des imports</h3>
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-white px-4 py-3 text-sm">
                <div><span className="font-bold">{r.filename}</span> <span className="text-muted-ink">• {r.kind === "historique" ? `${r.exploitations_applied || 0} exploitations` : `${r.created} spots`}</span></div>
                <span className="text-xs text-muted-ink">{frenchDate(r.created_at)} • {r.user_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ==================== Historique (fichier de suivi) ==================== */
function HistoryImportTab() {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attachUncertain, setAttachUncertain] = useState(false);
  const inputRef = useRef();

  const upload = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/imports/historical/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur de lecture du fichier de suivi"); }
    finally { setLoading(false); }
  };
  const loadProvided = async () => {
    setLoading(true);
    try { const { data } = await api.post("/imports/historical/preview-url", {}); setPreview(data); setStep(2); }
    catch (err) { toast.error(err?.response?.data?.detail || "Impossible de charger le fichier fourni."); }
    finally { setLoading(false); }
  };
  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/imports/historical/commit", { import_id: preview.import_id, attach_uncertain: attachUncertain });
      setResult(data); setStep(3);
      toast.success(`${data.bookings_created} journées intégrées au Planning`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la validation"); }
    finally { setCommitting(false); }
  };
  const reset = () => { setStep(1); setPreview(null); setResult(null); setAttachUncertain(false); };
  const r = preview?.report;

  return (
    <>
      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[rgba(238,114,46,0.28)] bg-[rgba(238,114,46,0.05)] p-4">
        <History className="mt-0.5 h-5 w-5 shrink-0 text-[var(--shariti-orange)]" />
        <div className="text-sm text-[rgba(24,24,24,0.75)]">
          <span className="font-bold text-ink">Reprise de l'historique réel.</span> Le fichier de suivi alimente à la fois l'historique des fiches spots ET la <span className="font-bold text-ink">grille Planning</span> : chaque journée-spot devient un booking réel (passé → <span className="font-semibold">Réalisé</span>, aujourd'hui/futur → <span className="font-semibold">Planifié</span>).
          Les libellés bruités (lignes, CDP, association) sont réconciliés automatiquement vers les référentiels ; les cas non résolus sont importés quand même et signalés « à réconcilier ». Les <span className="font-bold text-ink">données de démonstration du Planning sont remplacées</span>. Tout est marqué <span className="font-bold text-ink">historique</span> et purgeable depuis l'Administration.
        </div>
      </div>
      <Stepper step={step} labels={["Téléversement", "Analyse", "Import"]} />
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="h1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Dropzone loading={loading} inputRef={inputRef} onFile={upload} testId="hist-import" accept=".xlsx,.xlsm"
              title="Déposez le fichier de suivi (.xlsx) ici" subtitle="SUIVI BOOKING GLOBAL — 2026 & 2025 prioritaires, 2024 best effort" />
            <div className="mt-4 flex items-center justify-center">
              <Button variant="secondary" onClick={loadProvided} disabled={loading} className="border border-[var(--border-soft)] bg-white" data-testid="hist-load-provided">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <History className="mr-2 h-4 w-4" />} Charger le fichier de suivi fourni
              </Button>
            </div>
          </motion.div>
        )}
        {step === 2 && r && (
          <motion.div key="h2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-4">
              <CalendarClock className="h-6 w-6 text-[var(--shariti-orange)]" />
              <div className="flex-1"><div className="font-bold">{preview.filename}</div>
                <div className="text-xs text-muted-ink">{r.total_exploitations} exploitations extraites</div></div>
              {Object.entries(r.by_year || {}).sort().map(([y, n]) => (
                <span key={y} className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs font-bold" data-testid={`hist-year-${y}`}>{y} : {n}</span>
              ))}
            </div>

            <div className="grid gap-3 @2xl/page:grid-cols-2 @4xl/page:grid-cols-4">
              <MiniStat icon={Store} label="Spots reconnus (matchés)" value={r.spots_matched} color="#1F9D55" testId="hist-matched" />
              <MiniStat icon={MapPin} label="Spots à créer" value={r.spots_created} color="#2F6FED" testId="hist-created" />
              <MiniStat icon={GitCompareArrows} label="Cas incertains" value={r.spots_uncertain} color="#E9B800" testId="hist-uncertain" />
              <MiniStat icon={Users2} label="Associations à valider" value={r.assoc_uncertain} color="#D64545" testId="hist-assoc-uncertain" />
            </div>

            {/* Par feuille */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
              <h3 className="mb-3 text-sm font-bold">Exploitations par feuille</h3>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[10px] uppercase text-muted-ink">
                    <th className="py-1">Feuille</th><th className="py-1 text-right">Exploit.</th><th className="py-1 text-right">Matchés</th><th className="py-1 text-right">Nouveaux</th><th className="py-1 text-right">Incertains</th><th className="py-1 text-right">Lignes ign.</th></tr></thead>
                  <tbody>{(r.sheets || []).map((s, i) => (
                    <tr key={i} className="border-t border-[var(--border-soft)]" data-testid={`hist-sheet-${i}`}>
                      <td className="py-1 font-semibold">{s.sheet}</td>
                      <td className="py-1 text-right font-bold">{s.exploitations}</td>
                      <td className="py-1 text-right text-[#1F9D55]">{s.matched}</td>
                      <td className="py-1 text-right text-[#2F6FED]">{s.created}</td>
                      <td className="py-1 text-right text-[#B98900]">{s.uncertain}</td>
                      <td className="py-1 text-right text-muted-ink">{s.ignored_lines}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>

            {/* Couleurs CDP détectées */}
            {r.color_map && Object.keys(r.color_map).length > 0 && (
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><Palette className="h-4 w-4 text-[var(--shariti-orange)]" /> CDP identifiés par couleur (parsing uniquement)</h3>
                <p className="mb-3 text-[11px] text-muted-ink">Les couleurs de l'ancien fichier servent seulement à retrouver qui a booké. Les couleurs de la plateforme restent inchangées.</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(r.color_map).map(([rgb, cdp]) => (
                    <span key={rgb} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-semibold">
                      <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: `#${rgb}` }} />{cdp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Lignes ignorées + Associations incertaines */}
            <div className="grid gap-4 @3xl/page:grid-cols-2">
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-2 text-sm font-bold">Lignes ignorées (motif)</h3>
                <div className="space-y-1 text-xs">
                  {Object.entries(r.ignored || {}).map(([m, n]) => (
                    <div key={m} className="flex justify-between rounded bg-[var(--surface-2)] px-2 py-1"><span className="font-semibold">{m}</span><span className="text-muted-ink">{n}</span></div>
                  ))}
                  {Object.keys(r.ignored || {}).length === 0 && <div className="text-muted-ink">Aucune.</div>}
                </div>
              </div>
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-2 text-sm font-bold">Associations à valider</h3>
                <div className="max-h-40 space-y-1 overflow-y-auto text-xs">
                  {Object.entries(r.assoc_uncertain_labels || {}).sort((a, b) => b[1] - a[1]).map(([l, n]) => (
                    <div key={l} className="flex justify-between rounded bg-[rgba(214,69,69,0.06)] px-2 py-1"><span className="font-semibold">{l}</span><span className="text-muted-ink">{n}</span></div>
                  ))}
                  {Object.keys(r.assoc_uncertain_labels || {}).length === 0 && <div className="text-muted-ink">Toutes les associations ont été reconnues.</div>}
                </div>
              </div>
            </div>

            {/* Cas incertains (spots) */}
            {r.uncertain_samples?.length > 0 && (
              <details className="rounded-2xl border border-[rgba(233,184,0,0.45)] bg-white p-4" data-testid="hist-uncertain-details">
                <summary className="cursor-pointer text-sm font-bold text-[#5A4200]">Voir {r.uncertain_samples.length} cas de matching incertain à valider</summary>
                <div className="mt-2 max-h-60 space-y-1 overflow-y-auto text-xs">
                  {r.uncertain_samples.map((u, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded bg-[rgba(252,219,83,0.2)] px-2 py-1">
                      <span className="font-semibold">{u.raw}{u.ville ? ` (${u.ville})` : ""}</span>
                      <span className="text-muted-ink">≈ {u.best_match} · score {Math.round(u.score * 100)}%</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Commit controls */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-bold">Traitement des cas incertains</div>
                  <div className="text-xs text-muted-ink">Par défaut, les cas incertains sont créés comme nouveaux spots historiques (aucune fusion risquée). Activez pour les rattacher au meilleur match suggéré.</div>
                </div>
                <Switch checked={attachUncertain} onCheckedChange={setAttachUncertain} data-testid="hist-attach-uncertain" />
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={reset} data-testid="hist-cancel"><RotateCcw className="mr-2 h-4 w-4" /> Recommencer</Button>
              <Button onClick={commit} disabled={committing} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="hist-commit">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Intégrer {r.total_exploitations} exploitations <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </div>
          </motion.div>
        )}
        {step === 3 && result && (
          <SuccessCard title={`${result.bookings_created} journées intégrées au Planning`}
            subtitle={`${result.realise} réalisées (passé) • ${result.planifie} planifiées (futur) • ${result.avec_code_campagne} avec code campagne • ${result.lignes_created} lignes reconstituées • ${result.spots_created} spots historiques. La grille Planning affiche désormais l'historique réel (données démo remplacées).`}
            onReset={reset} testId="hist-success" primaryHref="/planning" primaryLabel="Ouvrir le Planning" />
        )}
      </AnimatePresence>
    </>
  );
}

/* ============================ Shared ============================ */
function Stepper({ step, labels }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      {labels.map((l, i) => {
        const n = i + 1;
        return (
          <div key={n} className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${step >= n ? "bg-[var(--shariti-orange)] text-white" : "bg-[var(--surface-2)] text-muted-ink"}`}>{n}</div>
            <span className={`text-sm font-semibold ${step >= n ? "text-ink" : "text-muted-ink"}`}>{l}</span>
            {i < labels.length - 1 && <div className="h-px w-8 bg-[var(--border-soft)]" />}
          </div>
        );
      })}
    </div>
  );
}

function Dropzone({ loading, inputRef, onFile, testId, accept, title, subtitle }) {
  return (
    <div onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      data-testid={`${testId}-dropzone`}
      className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[var(--border-soft)] bg-white px-6 py-16 text-center transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.03)]">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--surface-2)]">
        {loading ? <Loader2 className="h-7 w-7 animate-spin text-[var(--shariti-orange)]" /> : <UploadCloud className="h-7 w-7 text-[var(--shariti-orange)]" />}
      </div>
      <h3 className="mt-4 text-lg font-extrabold">{title}</h3>
      <p className="mt-1 text-sm text-muted-ink">{subtitle || "ou cliquez pour parcourir vos fichiers"}</p>
      <input ref={inputRef} type="file" accept={accept} className="hidden" data-testid={`${testId}-file-input`}
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
}

function SuccessCard({ title, subtitle, onReset, testId, primaryHref = "/spots", primaryLabel = "Ouvrir la banque de spots" }) {
  return (
    <motion.div key="s3" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center rounded-2xl border border-[var(--border-soft)] bg-white px-6 py-14 text-center">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
        className="flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(31,157,85,0.12)]">
        <CheckCircle2 className="h-10 w-10 text-[#1F9D55]" />
      </motion.div>
      <h3 className="mt-4 text-2xl font-extrabold" data-testid={testId}>{title}</h3>
      <p className="mt-1 max-w-md text-sm text-muted-ink">{subtitle}</p>
      <div className="mt-6 flex gap-2">
        <Button variant="secondary" onClick={onReset} className="border border-[var(--border-soft)] bg-white">Nouvel import</Button>
        <Button onClick={() => window.location.href = primaryHref} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">{primaryLabel}</Button>
      </div>
    </motion.div>
  );
}

function DetailsList({ items, label, render, danger, warn, testId }) {
  if (!items?.length) return null;
  const border = danger ? "border-[rgba(214,69,69,0.3)]" : warn ? "border-[rgba(233,184,0,0.45)]" : "border-[var(--border-soft)]";
  const sumColor = danger ? "text-[#9B2226]" : warn ? "text-[#5A4200]" : "";
  const bg = danger ? "bg-[rgba(214,69,69,0.06)]" : warn ? "bg-[rgba(252,219,83,0.2)]" : "bg-[var(--surface-2)]";
  return (
    <details className={`rounded-2xl border ${border} bg-white p-4`} data-testid={testId}>
      <summary className={`cursor-pointer text-sm font-bold ${sumColor}`}>Voir les {items.length} {label}</summary>
      <div className="mt-2 max-h-52 space-y-1 overflow-y-auto text-xs">
        {items.slice(0, 100).map((d, i) => {
          const [a, b] = render(d);
          return <div key={i} className={`flex justify-between rounded ${bg} px-2 py-1`}><span className="font-semibold">{a}</span><span className="text-muted-ink">{b}</span></div>;
        })}
      </div>
    </details>
  );
}

function MiniStat({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={testId}>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${color}18` }}><Icon className="h-4 w-4" style={{ color }} /></div>
      <div><div className="text-lg font-extrabold leading-none">{value}</div><div className="text-[11px] text-muted-ink">{label}</div></div>
    </div>
  );
}


/* ==================== Bulletins (Evergiving) ==================== */
const STATUT_TONE = {
  "Approuvé": "text-[#0F5A2F] bg-[rgba(31,157,85,0.12)]",
  "Invalide": "text-[#9B2226] bg-[rgba(214,69,69,0.12)]",
  "En attente": "text-[#5A4200] bg-[rgba(252,219,83,0.35)]",
  "Annulé": "text-[#9B2226] bg-[rgba(214,69,69,0.12)]",
  "Alien": "text-[rgba(24,24,24,0.6)] bg-[rgba(24,24,24,0.06)]",
};

function fmtNb(n) {
  return (n ?? 0).toLocaleString("fr-FR");
}

function EvergivingImportTab() {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attachUncertain, setAttachUncertain] = useState(true);
  const [createRds, setCreateRds] = useState(true);
  const [createAssocs, setCreateAssocs] = useState(true);
  const [decisions, setDecisions] = useState({});   // raw_key -> {action, spot_id, spot_label, binome_rd_map}
  const [selected, setSelected] = useState(() => new Set()); // raw_key sélectionnés (cases à cocher)
  const [minScore, setMinScore] = useState(70);      // seuil de score de confiance pour "Accepter les suggestions"
  const [sortMode, setSortMode] = useState("volume"); // volume | alpha | status
  const [isInitial, setIsInitial] = useState(false);
  const [lieuSearch, setLieuSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(60);
  const inputRef = useRef();

  const upload = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/imports/evergiving/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setDecisions({}); setSelected(new Set()); setIsInitial(!!data.suggested_initial); setLieuSearch(""); setVisibleCount(60); setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur de lecture du fichier Evergiving"); }
    finally { setLoading(false); }
  };
  const loadProvided = async () => {
    setLoading(true);
    try { const { data } = await api.post("/imports/evergiving/preview-url", {}); setPreview(data); setDecisions({}); setSelected(new Set()); setIsInitial(!!data.suggested_initial); setLieuSearch(""); setVisibleCount(60); setStep(2); }
    catch (err) { toast.error(err?.response?.data?.detail || "Impossible de charger le fichier fourni."); }
    finally { setLoading(false); }
  };
  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/imports/evergiving/commit", {
        import_id: preview.import_id,
        attach_uncertain: attachUncertain,
        create_missing_rds: createRds,
        create_missing_assocs: createAssocs,
        is_historical: isInitial,
        lieu_decisions: decisions,
      });
      setResult(data); setStep(3);
      toast.success(`${fmtNb(data.bulletins_created)} nouveaux · ${fmtNb(data.bulletins_updated)} mis à jour`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la validation"); }
    finally { setCommitting(false); }
  };
  const reset = () => { setStep(1); setPreview(null); setResult(null); setDecisions({}); };
  const setDecision = useCallback((key, dec) => {
    setDecisions((d) => {
      const next = { ...d };
      if (!dec) delete next[key]; else next[key] = dec;
      return next;
    });
  }, []);
  // Traitement en masse : applique une décision à une liste de libellés (respecte le filtre/la sélection courants).
  const bulkApply = useCallback((mode, targets, scoreThreshold = 0) => {
    setDecisions((d) => {
      const next = { ...d };
      for (const l of targets) {
        if (mode === "reset") { delete next[l.raw_key]; continue; }
        if (mode === "accept") {
          if (l.status === "uncertain" && l.suggested_spot_id && Math.round((l.score || 0) * 100) >= scoreThreshold) {
            next[l.raw_key] = { action: "attach", spot_id: l.suggested_spot_id, spot_label: l.suggested_name };
          }
        } else if (mode === "create") {
          next[l.raw_key] = { action: "create" };
        } else if (mode === "skip") {
          next[l.raw_key] = { action: "skip" };
        }
      }
      return next;
    });
    const affected = mode === "accept"
      ? targets.filter((l) => l.status === "uncertain" && l.suggested_spot_id && Math.round((l.score || 0) * 100) >= scoreThreshold).length
      : targets.length;
    const labels = { accept: "suggestion(s) acceptée(s)", create: "créé(s) en nouveau spot", skip: "ignoré(s)", reset: "réinitialisé(s)" };
    if (mode === "accept" && affected === 0) {
      toast.info(`Aucune suggestion ≥ ${scoreThreshold}% parmi les libellés ciblés.`);
    } else {
      toast.success(`${fmtNb(affected)} libellé(s) — ${labels[mode]}.`);
    }
  }, []);
  const toggleSelect = useCallback((key) => {
    setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const r = preview?.report;
  const inc = r?.incremental;
  const decidedCount = Object.keys(decisions).length;
  const toDecide = preview?.lieux_to_decide || [];
  const totalBull = toDecide.reduce((s, l) => s + (l.count || 0), 0);
  const decidedBull = toDecide.reduce((s, l) => s + (decisions[l.raw_key] ? (l.count || 0) : 0), 0);
  const pctBull = totalBull ? Math.round((decidedBull / totalBull) * 100) : 0;
  const searchQ = lieuSearch.trim().toLowerCase();
  const filteredLieux = (searchQ ? toDecide.filter((l) => l.raw_label.toLowerCase().includes(searchQ)) : toDecide);
  const sortedLieux = [...filteredLieux].sort((a, b) => {
    if (sortMode === "alpha") return a.raw_label.localeCompare(b.raw_label);
    if (sortMode === "status") {
      const rank = (x) => (x.status === "unmatched" ? 0 : 1);
      return rank(a) - rank(b) || (b.count - a.count);
    }
    return (b.count || 0) - (a.count || 0);
  });
  const visibleLieux = sortedLieux.slice(0, visibleCount);
  const selectionActive = selected.size > 0;
  const bulkTargets = selectionActive ? toDecide.filter((l) => selected.has(l.raw_key)) : filteredLieux;
  const allFilteredSelected = filteredLieux.length > 0 && filteredLieux.every((l) => selected.has(l.raw_key));
  const someFilteredSelected = !allFilteredSelected && filteredLieux.some((l) => selected.has(l.raw_key));
  const toggleSelectAllFiltered = () => {
    setSelected((s) => {
      const n = new Set(s);
      const all = filteredLieux.length > 0 && filteredLieux.every((l) => n.has(l.raw_key));
      if (all) filteredLieux.forEach((l) => n.delete(l.raw_key));
      else filteredLieux.forEach((l) => n.add(l.raw_key));
      return n;
    });
  };

  return (
    <>
      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[rgba(31,157,85,0.28)] bg-[rgba(31,157,85,0.05)] p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#1F9D55]" />
        <div className="text-sm text-[rgba(24,24,24,0.75)]">
          <span className="font-bold text-ink">Confidentialité garantie.</span> L'identifiant donateur n'est jamais conservé (seulement un identifiant anonyme pour le dédoublonnage) ;
          l'adresse complète et les notes privées ne sont pas importées — uniquement code postal, ville, âge, genre, montant, statut et code RD.
          Le fichier ne contient que la date du don : aucun horaire n'est inventé. Toutes les données sont marquées <span className="font-bold text-ink">historiques</span> et purgeables depuis l'Administration.
        </div>
      </div>
      <Stepper step={step} labels={["Téléversement", "Validation", "Import"]} />
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="e1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Dropzone loading={loading} inputRef={inputRef} onFile={upload} testId="evg-import" accept=".xls,.xlsx"
              title="Déposez l'export Evergiving (.xls) ici" subtitle="1 ligne = 1 bulletin — suivi de performance par RD, spot et créneau" />
            <div className="mt-4 flex items-center justify-center">
              <Button variant="secondary" onClick={loadProvided} disabled={loading} className="border border-[var(--border-soft)] bg-white" data-testid="evg-load-provided">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HeartHandshake className="mr-2 h-4 w-4" />} Charger le fichier Evergiving fourni
              </Button>
            </div>
          </motion.div>
        )}
        {step === 2 && r && (
          <motion.div key="e2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-4">
              <HeartHandshake className="h-6 w-6 text-[var(--shariti-orange)]" />
              <div className="flex-1"><div className="font-bold">{preview.filename}</div>
                <div className="text-xs text-muted-ink">{fmtNb(r.imported)} bulletins prêts à l'import</div></div>
              {Object.entries(r.by_year || {}).sort().map(([y, n]) => (
                <span key={y} className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs font-bold" data-testid={`evg-year-${y}`}>{y} : {fmtNb(n)}</span>
              ))}
            </div>

            {inc && (
              <div className="rounded-2xl border border-[rgba(47,111,237,0.28)] bg-[rgba(47,111,237,0.04)] p-4" data-testid="evg-incremental">
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-ink"><GitCompareArrows className="h-4 w-4 text-[#2F6FED]" /> Import incrémental (idempotent)</div>
                <div className="grid grid-cols-2 gap-2 @4xl/page:grid-cols-4">
                  <IncStat label="Nouveaux" value={inc.nouveaux} color="#1F9D55" testId="evg-inc-new" />
                  <IncStat label="Mis à jour" value={inc.mis_a_jour} color="#2F6FED" testId="evg-inc-upd" />
                  <IncStat label="Inchangés (ignorés)" value={inc.inchanges} color="#6B7280" testId="evg-inc-same" />
                  <IncStat label="Doublons du fichier" value={inc.doublons_fichier} color="#E9B800" testId="evg-inc-dup" />
                </div>
                <p className="mt-2 text-[11px] text-muted-ink">
                  {r.already_imported > 0
                    ? `${fmtNb(r.already_imported)} bulletins déjà présents. Seuls les nouveaux sont créés ; les statuts modifiés sont mis à jour. Aucun doublon, aucune purge nécessaire.`
                    : "Premier import : tous les bulletins seront créés."}
                </p>
              </div>
            )}

            <div className="grid gap-3 @2xl/page:grid-cols-2 @4xl/page:grid-cols-4">
              <MiniStat icon={Users2} label="RD à créer (statut à compléter)" value={fmtNb(r.rds_to_create_count)} color="#2F6FED" testId="evg-rds" />
              <MiniStat icon={HeartHandshake} label="Associations à créer" value={fmtNb((r.assocs_to_create || []).length)} color="#EE722E" testId="evg-assocs" />
              <MiniStat icon={Euro} label="Montant mensuel cumulé" value={`${fmtNb(Math.round(r.montant_total))} €`} color="#8B5CF6" testId="evg-montant" />
            </div>

            {/* Lieux */}
            <div className="grid gap-3 @3xl/page:grid-cols-3">
              <MiniStat icon={Store} label="Lieux reconnus (auto)" value={fmtNb(r.lieux_matched + (r.lieux_known || 0))} color="#1F9D55" testId="evg-lieux-matched" />
              <MiniStat icon={GitCompareArrows} label="Lieux incertains" value={fmtNb(r.lieux_uncertain)} color="#E9B800" testId="evg-lieux-uncertain" />
              <MiniStat icon={MapPin} label="Lieux non reconnus" value={fmtNb(r.lieux_unmatched)} color="#D64545" testId="evg-lieux-unmatched" />
            </div>

            {/* Répartitions */}
            <div className="grid gap-4 @3xl/page:grid-cols-2">
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-3 text-sm font-bold">Bulletins par association</h3>
                <div className="space-y-1.5 text-xs">
                  {Object.entries(r.by_association || {}).sort((a, b) => b[1] - a[1]).map(([a, n]) => (
                    <div key={a} className="flex items-center justify-between rounded bg-[var(--surface-2)] px-2.5 py-1.5" data-testid={`evg-assoc-${a}`}>
                      <span className="font-semibold">{a}</span><span className="font-extrabold">{fmtNb(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-3 text-sm font-bold">Bulletins par statut</h3>
                <div className="space-y-1.5 text-xs">
                  {Object.entries(r.by_statut || {}).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                    <div key={s} className="flex items-center justify-between rounded px-2.5 py-1.5 font-semibold" data-testid={`evg-statut-${s}`}>
                      <span className={`rounded px-2 py-0.5 ${STATUT_TONE[s] || "bg-[var(--surface-2)]"}`}>{s}</span>
                      <span className="font-extrabold text-ink">{fmtNb(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Associations à créer */}
            {(r.assocs_to_create || []).length > 0 && (
              <div className="rounded-2xl border border-[rgba(238,114,46,0.28)] bg-white p-5" data-testid="evg-assocs-to-create">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold"><HeartHandshake className="h-4 w-4 text-[var(--shariti-orange)]" /> Associations créées automatiquement (à compléter)</h3>
                <div className="flex flex-wrap gap-2">
                  {r.assocs_to_create.map((a) => (
                    <span key={a.acronym} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-semibold">
                      <Badge className="bg-[var(--shariti-orange)] text-white">{a.acronym}</Badge>{a.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Options globales */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
              <h3 className="mb-3 text-sm font-bold">Options d'import</h3>
              <div className="space-y-3">
                <ToggleRow label="Rattrapage initial (marquer comme données historiques)" hint="À activer uniquement pour le tout premier import de reprise. Les ré-imports hebdomadaires doivent rester non-historiques."
                  checked={isInitial} onChange={setIsInitial} testId="evg-opt-initial" />
                <ToggleRow label="Rattacher automatiquement les lieux incertains au meilleur match" hint="Sinon, un nouveau spot historique est créé pour chaque lieu incertain."
                  checked={attachUncertain} onChange={setAttachUncertain} testId="evg-opt-attach" />
                <ToggleRow label="Créer les RD manquants (statut « à compléter »)" hint="341 RD max, enrichissables depuis l'Administration."
                  checked={createRds} onChange={setCreateRds} testId="evg-opt-rds" />
                <ToggleRow label="Créer les associations manquantes" hint="Les ONG absentes du référentiel sont créées (à compléter)."
                  checked={createAssocs} onChange={setCreateAssocs} testId="evg-opt-assocs" />
              </div>
            </div>

            {/* Validation des lieux */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-bold"><GitCompareArrows className="h-4 w-4 text-[var(--shariti-orange)]" /> Validation des lieux</h3>
                <div className="flex items-center gap-2">
                  <div className="flex overflow-hidden rounded-lg border border-[var(--border-soft)]" data-testid="evg-sort-control">
                    {[["volume", "Volume"], ["alpha", "A–Z"], ["status", "Statut"]].map(([m, lab]) => (
                      <button key={m} onClick={() => setSortMode(m)} data-testid={`evg-sort-${m}`}
                        className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${sortMode === m ? "bg-[var(--shariti-orange)] text-white" : "bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>{lab}</button>
                    ))}
                  </div>
                  <Badge variant="secondary" className="bg-[var(--surface-2)]" data-testid="evg-decided-count">{decidedCount} ajusté(s)</Badge>
                </div>
              </div>
              <p className="mb-3 text-[11px] text-muted-ink">
                <span className="font-semibold text-ink">{fmtNb(preview.lieux_to_decide_total)}</span> libellés à valider, <span className="font-semibold text-ink">classés par volume de bulletins décroissant</span>. Chaque libellé est traité <span className="font-semibold text-ink">en une fois</span> (tous ses bulletins routés d'un coup) et chaque décision est mémorisée pour les prochains imports. Validez d'abord les plus gros : ils couvrent l'essentiel des bulletins.
              </p>
              {toDecide.length > 0 && (
                <div className="mb-3 rounded-xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="evg-progress">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold">
                    <span data-testid="evg-progress-labels">{fmtNb(decidedCount)} / {fmtNb(toDecide.length)} libellés traités</span>
                    <span className="text-base font-extrabold text-[var(--shariti-orange)]" data-testid="evg-progress-coverage">{pctBull}%</span>
                  </div>
                  <Progress value={pctBull} className="h-2.5" />
                  <div className="mt-1.5 text-[11px] text-muted-ink">des bulletins à valider sont couverts par vos décisions ({fmtNb(decidedBull)} / {fmtNb(totalBull)} bulletins).</div>
                </div>
              )}
              {toDecide.length > 0 && (
                <div className="relative mb-3">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                  <input value={lieuSearch} onChange={(e) => { setLieuSearch(e.target.value); setVisibleCount(60); }}
                    placeholder="Rechercher un libellé de lieu…" data-testid="evg-lieu-search"
                    className="h-9 w-full rounded-lg border border-[var(--border-soft)] bg-white pl-8 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/30" />
                </div>
              )}
              {toDecide.length > 0 && sortedLieux.length > 0 && (
                <div className="mb-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="evg-bulk-bar">
                  <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Actions en masse</span>
                    <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-ink">
                      <Checkbox checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)} onCheckedChange={toggleSelectAllFiltered} data-testid="evg-select-all" />
                      Tout sélectionner{searchQ ? " (filtré)" : ""}
                    </label>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-ink" data-testid="evg-bulk-scope">
                      {selectionActive ? `${fmtNb(selected.size)} sélectionné(s)` : `${fmtNb(filteredLieux.length)} libellé(s)${searchQ ? " filtré(s)" : ""}`}
                    </span>
                    {selectionActive && (
                      <button onClick={clearSelection} className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="evg-clear-selection">Effacer la sélection</button>
                    )}
                    <span className="ml-auto text-[11px] text-muted-ink">
                      Cible&nbsp;: <span className="font-semibold text-ink">{selectionActive ? "sélection" : (searchQ ? "libellés filtrés" : "tous les libellés")}</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2" data-testid="evg-score-threshold" title="« Accepter les suggestions » ne s'applique qu'aux suggestions dont le score de confiance est ≥ à ce seuil.">
                      <span className="text-[11px] font-semibold text-muted-ink">Seuil de confiance</span>
                      <Slider value={[minScore]} onValueChange={(v) => setMinScore(v[0])} min={0} max={100} step={5} className="w-28" data-testid="evg-score-slider" />
                      <span className="w-10 text-[11px] font-bold text-ink" data-testid="evg-score-value">≥ {minScore}%</span>
                    </div>
                    <div className="ml-auto flex flex-wrap items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => bulkApply("accept", bulkTargets, minScore)} className="h-8 border-[var(--border-soft)] bg-white" data-testid="evg-bulk-accept">
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5 text-[#1D4ED8]" /> Accepter les suggestions
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => bulkApply("create", bulkTargets)} className="h-8 border-[var(--border-soft)] bg-white" data-testid="evg-bulk-create">
                        <Plus className="mr-1 h-3.5 w-3.5 text-[#0F5A2F]" /> Créer en nouveaux spots
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => bulkApply("skip", bulkTargets)} className="h-8 border-[var(--border-soft)] bg-white" data-testid="evg-bulk-skip">
                        <Ban className="mr-1 h-3.5 w-3.5 text-muted-ink" /> Ignorer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => bulkApply("reset", bulkTargets)} className="h-8" data-testid="evg-bulk-reset">
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Réinitialiser
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {(toDecide.length === 0) ? (
                <div className="rounded-lg bg-[rgba(31,157,85,0.08)] px-3 py-2 text-xs font-semibold text-[#0F5A2F]">Tous les lieux ont été reconnus automatiquement (aucune validation requise).</div>
              ) : sortedLieux.length === 0 ? (
                <div className="rounded-lg bg-[var(--surface-2)] px-3 py-4 text-center text-xs text-muted-ink">Aucun libellé ne correspond à « {lieuSearch} ».</div>
              ) : (
                <>
                  <div className="max-h-[420px] w-full overflow-y-auto overflow-x-hidden rounded-lg border border-[var(--border-soft)]" data-testid="evg-lieux-list">
                    <div className="divide-y divide-[var(--border-soft)]">
                      {visibleLieux.map((l) => (
                        <LieuDecisionRow key={l.raw_key} lieu={l} decision={decisions[l.raw_key]}
                          attachUncertain={attachUncertain} onChange={setDecision}
                          selected={selected.has(l.raw_key)} onToggleSelect={toggleSelect} />
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-muted-ink">
                    <span>{fmtNb(Math.min(visibleCount, sortedLieux.length))} / {fmtNb(sortedLieux.length)} affichés</span>
                    {visibleCount < sortedLieux.length && (
                      <Button variant="ghost" size="sm" onClick={() => setVisibleCount((c) => c + 100)} data-testid="evg-load-more">
                        Charger plus (+100)
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={reset} data-testid="evg-cancel"><RotateCcw className="mr-2 h-4 w-4" /> Recommencer</Button>
              <Button onClick={commit} disabled={committing} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="evg-commit">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Intégrer {fmtNb(r.imported)} bulletins <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </div>
          </motion.div>
        )}
        {step === 3 && result && (
          <SuccessCard title={`${fmtNb(result.bulletins_created)} nouveaux bulletins${result.bulletins_updated ? `, ${fmtNb(result.bulletins_updated)} mis à jour` : ""}`}
            subtitle={`${fmtNb(result.bulletins_unchanged)} inchangés (ignorés) • ${fmtNb(result.rds_created)} RD créés • ${fmtNb(result.assocs_created)} associations • ${fmtNb(result.spots_created)} spots${result.unresolved_spot ? ` • ${fmtNb(result.unresolved_spot)} sans lieu` : ""}${result.is_historical ? " • marqués historiques" : ""}.`}
            onReset={reset} testId="evg-success" />
        )}
      </AnimatePresence>
    </>
  );
}

function IncStat({ label, value, color, testId }) {
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2" data-testid={testId}>
      <div className="text-lg font-extrabold leading-none" style={{ color }}>{(value ?? 0).toLocaleString("fr-FR")}</div>
      <div className="mt-0.5 text-[10px] text-muted-ink">{label}</div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div><div className="text-sm font-semibold">{label}</div><div className="text-[11px] text-muted-ink">{hint}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function LieuDecisionRow({ lieu, decision, attachUncertain, onChange, selected, onToggleSelect }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [binomeOpen, setBinomeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const uncertain = lieu.status === "uncertain";

  let effective;
  if (decision?.action === "attach") effective = { tone: "attach", text: `→ ${decision.spot_label || "spot choisi"}` };
  else if (decision?.action === "binome") effective = { tone: "binome", text: `Binôme (${Object.keys(decision.binome_rd_map || {}).length} RD)` };
  else if (decision?.action === "skip") effective = { tone: "skip", text: "Ignoré" };
  else if (decision?.action === "create") effective = { tone: "create", text: "Nouveau spot" };
  else if (uncertain && attachUncertain && lieu.suggested_spot_id) effective = { tone: "auto", text: `Auto → ${lieu.suggested_name}` };
  else effective = { tone: "auto", text: "Auto → nouveau spot" };

  const toneCls = {
    attach: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8] border-[rgba(47,111,237,0.3)]",
    binome: "bg-[rgba(139,92,246,0.14)] text-[#6D28D9] border-[rgba(139,92,246,0.3)]",
    skip: "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.6)] border-[var(--border-soft)]",
    create: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] border-[rgba(31,157,85,0.3)]",
    auto: "bg-[var(--surface-2)] text-muted-ink border-[var(--border-soft)]",
  }[effective.tone];

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${selected ? "bg-[rgba(238,114,46,0.07)]" : ""}`} data-testid={`evg-lieu-${lieu.raw_key}`}>
      <Checkbox checked={!!selected} onCheckedChange={() => onToggleSelect(lieu.raw_key)} className="shrink-0" data-testid={`evg-lieu-select-${lieu.raw_key}`} aria-label={`Sélectionner ${lieu.raw_label}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{lieu.raw_label}</span>
          <Badge variant="secondary" className="shrink-0 bg-[var(--surface-2)] text-[10px]">{fmtNb(lieu.count)} bull.</Badge>
          {uncertain
            ? <Badge className="shrink-0 bg-[rgba(233,184,0,0.35)] text-[#5A4200] text-[10px]">incertain</Badge>
            : <Badge className="shrink-0 bg-[rgba(214,69,69,0.12)] text-[#9B2226] text-[10px]">non reconnu</Badge>}
        </div>
        {uncertain && lieu.suggested_name && (
          <div className="mt-0.5 truncate text-[11px] text-muted-ink">Suggestion : {lieu.suggested_name} · {Math.round((lieu.score || 0) * 100)}%</div>
        )}
      </div>
      <span className={`inline-block max-w-[150px] shrink-0 truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold ${toneCls}`} title={effective.text} data-testid={`evg-lieu-badge-${lieu.raw_key}`}>{effective.text}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 shrink-0 border-[var(--border-soft)]" data-testid={`evg-lieu-menu-${lieu.raw_key}`}>
            Décision <ChevronDown className="ml-1 h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => onChange(lieu.raw_key, { action: "create" })} data-testid={`evg-lieu-create-${lieu.raw_key}`}>
            <Plus className="mr-2 h-4 w-4 text-[#0F5A2F]" /> Créer un spot (historique)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCreateOpen(true)} data-testid={`evg-lieu-createnow-${lieu.raw_key}`}>
            <Building2 className="mr-2 h-4 w-4 text-[#0F5A2F]" /> Créer le spot dans la banque…
          </DropdownMenuItem>
          {uncertain && lieu.suggested_spot_id && (
            <DropdownMenuItem onClick={() => onChange(lieu.raw_key, { action: "attach", spot_id: lieu.suggested_spot_id, spot_label: lieu.suggested_name })} data-testid={`evg-lieu-accept-${lieu.raw_key}`}>
              <CheckCircle2 className="mr-2 h-4 w-4 text-[#1D4ED8]" /> Accepter la suggestion
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setAttachOpen(true)} data-testid={`evg-lieu-attach-${lieu.raw_key}`}>
            <Link2 className="mr-2 h-4 w-4 text-[#1D4ED8]" /> Rattacher à un spot…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setBinomeOpen(true)} disabled={!lieu.rd_codes?.length} data-testid={`evg-lieu-binome-${lieu.raw_key}`}>
            <Split className="mr-2 h-4 w-4 text-[#6D28D9]" /> Binôme (2 emplacements)…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onChange(lieu.raw_key, { action: "skip" })} data-testid={`evg-lieu-skip-${lieu.raw_key}`}>
            <Ban className="mr-2 h-4 w-4 text-muted-ink" /> Ignorer ce lieu
          </DropdownMenuItem>
          {decision && (
            <DropdownMenuItem onClick={() => onChange(lieu.raw_key, null)} data-testid={`evg-lieu-reset-${lieu.raw_key}`}>
              <RotateCcw className="mr-2 h-4 w-4 text-muted-ink" /> Réinitialiser (auto)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SpotPickerDialog open={attachOpen} onOpenChange={setAttachOpen} title={`Rattacher « ${lieu.raw_label} »`}
        onSelect={(s) => { onChange(lieu.raw_key, { action: "attach", spot_id: s.id, spot_label: s.normalized_name }); setAttachOpen(false); }} />
      <BinomeDialog open={binomeOpen} onOpenChange={setBinomeOpen} lieu={lieu}
        onConfirm={(map) => { onChange(lieu.raw_key, { action: "binome", binome_rd_map: map }); setBinomeOpen(false); }} />
      <CreateSpotDialog open={createOpen} onOpenChange={setCreateOpen} lieu={lieu}
        onCreated={(s) => { onChange(lieu.raw_key, { action: "attach", spot_id: s.id, spot_label: s.normalized_name }); setCreateOpen(false); }} />
    </div>
  );
}

function useSpotSearch(open) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let active = true; setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/spots", { params: { search: q, limit: 20 } });
        if (active) setResults(data);
      } catch { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, open]);
  return { q, setQ, results, loading };
}

function SpotList({ open, onSelect, testId }) {
  const { q, setQ, results, loading } = useSpotSearch(open);
  return (
    <Command shouldFilter={false} className="rounded-lg border border-[var(--border-soft)]">
      <CommandInput placeholder="Rechercher un spot (nom, ville, dépt.)…" value={q} onValueChange={setQ} data-testid={`${testId}-input`} />
      <CommandList>
        {loading && <div className="py-4 text-center text-xs text-muted-ink">Recherche…</div>}
        {!loading && <CommandEmpty>Aucun spot trouvé.</CommandEmpty>}
        <CommandGroup>
          {results.map((s) => (
            <CommandItem key={s.id} value={s.id} onSelect={() => onSelect(s)} data-testid={`${testId}-opt-${s.id}`}>
              <Store className="mr-2 h-3.5 w-3.5 text-[var(--shariti-orange)]" />
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold">{s.normalized_name}</div>
                <div className="truncate text-[10px] text-muted-ink">{[s.ville, s.dept].filter(Boolean).join(" · ")}</div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function SpotPickerDialog({ open, onOpenChange, title, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="evg-spot-picker">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription>Choisissez le spot de la banque auquel rattacher les bulletins de ce lieu.</DialogDescription>
        </DialogHeader>
        <SpotList open={open} onSelect={onSelect} testId="evg-attach-search" />
      </DialogContent>
    </Dialog>
  );
}

function BinomeDialog({ open, onOpenChange, lieu, onConfirm }) {
  const [spotA, setSpotA] = useState(null);
  const [spotB, setSpotB] = useState(null);
  const [pickOpen, setPickOpen] = useState(null); // 'A' | 'B' | null
  const [assign, setAssign] = useState({});       // rd_code -> 'A' | 'B'

  useEffect(() => { if (open) { setSpotA(null); setSpotB(null); setAssign({}); setPickOpen(null); } }, [open]);

  const rdCodes = lieu.rd_codes || [];
  const ready = spotA && spotB && rdCodes.every((c) => assign[c]);
  const confirm = () => {
    const map = {};
    rdCodes.forEach((c) => { map[c] = assign[c] === "B" ? spotB.id : spotA.id; });
    onConfirm(map);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="evg-binome-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Lieu binôme — « {lieu.raw_label} »</DialogTitle>
          <DialogDescription>Ce libellé regroupe 2 emplacements. Choisissez les 2 spots, puis affectez chaque RD au bon emplacement. L'attribution du bulletin reste celle du fichier (Code Recruteur).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {["A", "B"].map((slot) => {
            const spot = slot === "A" ? spotA : spotB;
            return (
              <div key={slot} className="rounded-lg border border-[var(--border-soft)] p-3">
                <div className="mb-1.5 text-xs font-bold text-muted-ink">Emplacement {slot}</div>
                <Button variant="outline" size="sm" className="w-full justify-start border-[var(--border-soft)] text-left" onClick={() => setPickOpen(slot)} data-testid={`evg-binome-pick-${slot}`}>
                  <Store className="mr-1.5 h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                  <span className="truncate text-xs">{spot ? spot.normalized_name : "Choisir un spot"}</span>
                </Button>
              </div>
            );
          })}
        </div>
        {pickOpen && (
          <SpotList open={!!pickOpen} testId={`evg-binome-search-${pickOpen}`}
            onSelect={(s) => { if (pickOpen === "A") setSpotA(s); else setSpotB(s); setPickOpen(null); }} />
        )}
        <div>
          <div className="mb-1.5 text-xs font-bold">Affectation des RD ({rdCodes.length})</div>
          <ScrollArea className="h-40 rounded-lg border border-[var(--border-soft)] p-1">
            {rdCodes.map((c) => (
              <div key={c} className="flex items-center justify-between px-2 py-1.5 text-xs">
                <span className="font-semibold">{c}</span>
                <div className="flex gap-1">
                  {["A", "B"].map((slot) => (
                    <button key={slot} onClick={() => setAssign((a) => ({ ...a, [c]: slot }))}
                      data-testid={`evg-binome-assign-${c}-${slot}`}
                      className={`rounded px-2 py-0.5 text-[11px] font-bold border ${assign[c] === slot
                        ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"
                        : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>{slot}</button>
                  ))}
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="evg-binome-cancel">Annuler</Button>
          <Button onClick={confirm} disabled={!ready} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="evg-binome-confirm">Valider le binôme</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateSpotDialog({ open, onOpenChange, lieu, onCreated }) {
  const [name, setName] = useState("");
  const [ville, setVille] = useState("");
  const [dept, setDept] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const label = lieu.raw_label || "";
    const m = label.match(/\((\d{2,3})\)/);
    const clean = label.replace(/\(\d{2,3}\)/, "").trim();
    setName(clean.toUpperCase());
    setVille(clean);
    setDept(m ? m[1].slice(0, 2) : "");
  }, [open, lieu.raw_label]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Le nom du spot est requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/spots", {
        normalized_name: name.trim().toUpperCase(), ville: ville.trim() || null,
        dept: dept.trim() || null, status: "jamais_appele", force: true,
      });
      toast.success(`Spot créé : ${data.normalized_name}`);
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la création du spot"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="evg-create-spot-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Créer le spot — « {lieu.raw_label} »</DialogTitle>
          <DialogDescription>Crée un vrai spot dans la banque (pré-rempli depuis le libellé) et y rattache les bulletins de ce lieu.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">Nom normalisé du spot</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9" data-testid="evg-create-spot-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Ville</Label>
              <Input value={ville} onChange={(e) => setVille(e.target.value)} className="mt-1 h-9" data-testid="evg-create-spot-ville" />
            </div>
            <div>
              <Label className="text-[11px]">Département</Label>
              <Input value={dept} onChange={(e) => setDept(e.target.value)} className="mt-1 h-9" data-testid="evg-create-spot-dept" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="evg-create-spot-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="evg-create-spot-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer et rattacher"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
